import { Icon } from './icon';
import styles from './source-link.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

interface SourceLinkProps {
  /**
   * Pre-translated label — "Imported from Instagram", "Imported from a photo".
   * The caller owns i18n, matching RelatedRecipeLink and every other element in
   * this directory.
   */
  label: string;
  /**
   * Where the recipe came from. Null for an import with no address of its own
   * (a photo, a PDF, pasted text) — those render as a plain label rather than a
   * dead link, since a link that goes nowhere is worse than no link.
   */
  href?: string | null;
  /**
   * Accessible name for the link. Required rather than derived: the visible
   * label reads as a fragment, and it should be announced as one sentence that
   * also says the link leaves the site.
   */
  ariaLabel?: string;
  className?: string;
}

/**
 * SourceLink — provenance for a recipe that came from somewhere else (#804).
 *
 * ONE definition for every import path. Whether the source is linkable is a
 * prop, not a second component: the affordance is the same line of text either
 * way, and only its interactivity differs.
 *
 * Styled to sit in the hero eyebrow beside the byline, at the same quiet weight
 * — provenance belongs next to authorship, and neither should compete with the
 * dish title.
 *
 * Outbound by definition, so the link carries `rel="noopener noreferrer"` and an
 * external-link glyph. It is a plain <a>, not next/link: these targets are other
 * people's sites, which the router has no business prefetching.
 */
export const SourceLink = ({ label, href, ariaLabel, className }: SourceLinkProps) => {
  if (!href) {
    return <span className={cx(styles.source, className)}>{label}</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel ?? label}
      className={cx(styles.source, styles.link, className)}
    >
      {label}
      <Icon name="external-link" size={12} className={styles.icon} aria-hidden="true" />
    </a>
  );
};
